/**
 * 工作流空状态组件
 */

import { Workflow, Settings } from 'lucide-react'

import { EmptyState } from '@/components/common/empty-state'
import { cn } from '@/lib/utils'

// ==================== 类型定义 ====================

interface WorkflowEmptyStateProps {
  type?: 'not-configured' | 'loading' | 'error'
  onConfigureClick?: () => void
  className?: string
}

// ==================== 组件实现 ====================

export function WorkflowEmptyState({
  type = 'not-configured',
  onConfigureClick,
  className,
}: WorkflowEmptyStateProps) {
  if (type === 'loading') {
    return (
      <div className={cn('bg-card rounded-lg border p-8', className)}>
        <div className="flex flex-col items-center justify-center text-center">
          <div className="bg-muted mb-4 animate-pulse rounded-full p-4">
            <Workflow className="text-muted-foreground h-8 w-8" />
          </div>
          <p className="text-muted-foreground text-sm">加载工作流中...</p>
        </div>
      </div>
    )
  }

  if (type === 'error') {
    return (
      <div className={cn('bg-card rounded-lg border', className)}>
        <EmptyState
          type="error"
          title="加载失败"
          description="无法加载工作流配置，请稍后重试"
          icon={Workflow}
          iconSize="sm"
        />
      </div>
    )
  }

  return (
    <div className={cn('bg-card rounded-lg border', className)}>
      <EmptyState
        type="first-time"
        title="工作流尚未配置"
        description="配置工作流后可追踪任务进展，管理开发流程"
        icon={Workflow}
        iconSize="sm"
        primaryAction={
          onConfigureClick
            ? {
                label: '配置工作流',
                onClick: onConfigureClick,
                icon: Settings,
              }
            : undefined
        }
      />
    </div>
  )
}

/**
 * 工作流引导提示
 * 显示在工作流面板顶部的简单提示
 */
interface WorkflowGuideProps {
  message: string
  actionLabel?: string
  onAction?: () => void
  variant?: 'info' | 'warning' | 'success'
  className?: string
}

export function WorkflowGuide({
  message,
  actionLabel,
  onAction,
  variant = 'info',
  className,
}: WorkflowGuideProps) {
  const variantClasses = {
    info: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    warning:
      'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    success:
      'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400',
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-md border p-3',
        variantClasses[variant],
        className
      )}
    >
      <span className="text-sm">{message}</span>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="shrink-0 text-sm font-medium underline-offset-4 hover:underline"
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}
